import { useMemo, useState, type CSSProperties } from 'react';
import { ChevronDown, ChevronRight, Palette } from 'lucide-react';
import { useStore, selectActiveNodes, isMechanicalItemNode } from '../store';
import { buildMechanicalColors } from '../mechanicalColors';

/**
 * Colour legend for the mechanical layout — one row per distinct part type
 * (colour swatch · name · quantity). Floats in the bottom-left corner, is
 * collapsible, and scrolls when there are many distinct parts. Hidden when the
 * sheet has no mechanical items.
 */
export function MechanicalLegend() {
  const nodes = useStore(selectActiveNodes);
  const [open, setOpen] = useState(true);

  const legend = useMemo(
    () => buildMechanicalColors(nodes.filter(isMechanicalItemNode)).legend,
    [nodes],
  );

  if (legend.length === 0) return null;
  const totalQty = legend.reduce((s, e) => s + e.qty, 0);

  return (
    <div style={panelStyle}>
      <button style={headerStyle} onClick={() => setOpen((o) => !o)} title="Toggle colour legend">
        {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        <Palette size={13} />
        <span style={{ flex: 1, textAlign: 'left' }}>Legend</span>
        <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>
          {legend.length} type{legend.length === 1 ? '' : 's'} · {totalQty}
        </span>
      </button>
      {open && (
        <div style={listStyle}>
          {legend.map((e) => (
            <div key={e.key} style={rowStyle} title={`${e.label} — ${e.qty}`}>
              <span style={{ ...swatchStyle, background: e.color }} />
              <span style={labelStyle}>{e.label}</span>
              <span style={qtyStyle}>×{e.qty}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const panelStyle: CSSProperties = {
  position: 'absolute',
  right: 12,
  top: 12,
  zIndex: 8,
  width: 220,
  maxHeight: '60%',
  display: 'flex',
  flexDirection: 'column',
  background: 'var(--panel)',
  border: '1px solid var(--panel-border-strong)',
  borderRadius: 'var(--radius)',
  boxShadow: 'var(--shadow)',
  overflow: 'hidden',
  fontSize: 11,
};

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '6px 8px',
  background: 'linear-gradient(var(--chrome-top), var(--chrome-bottom))',
  borderBottom: '1px solid var(--chrome-border)',
  color: 'var(--text)',
  fontWeight: 800,
  fontSize: 10.5,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  cursor: 'pointer',
  border: 'none',
  width: '100%',
};

const listStyle: CSSProperties = {
  overflowY: 'auto',
  padding: 4,
};

const rowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 7,
  padding: '3px 5px',
  borderRadius: 'var(--radius-sm)',
};

const swatchStyle: CSSProperties = {
  width: 12,
  height: 12,
  borderRadius: 2,
  flexShrink: 0,
  border: '1px solid rgba(0,0,0,0.35)',
};

const labelStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  color: 'var(--text)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const qtyStyle: CSSProperties = {
  color: 'var(--text-muted)',
  fontWeight: 700,
  fontFamily: 'ui-monospace, Consolas, monospace',
};
